<script setup>
import { reactive, computed, watch, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import {
  CAMPAIGN_DYNAMIC_VARIABLES,
  VARIABLE_CHIP_CLASS,
  VARIABLE_CHIP_HIGHLIGHTED_CLASS,
} from 'dashboard/components-next/Campaigns/shared/campaignVariables';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['edit', 'create'].includes(value),
  },
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const formState = {
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  conversationLabelId: null,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const isCsvCampaign = computed(
  () => props.selectedCampaign?.audience_type === 'csv_import'
);

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required: requiredIf(() => props.mode === 'create') },
  scheduledAt: { required },
  selectedAudience: {
    required: requiredIf(() => !isCsvCampaign.value),
  },
}));

const v$ = useVuelidate(rules, state);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name;

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const isEditMode = computed(() => props.mode === 'edit');

// In edit mode, reconstruct template object from campaign data for the parser
const editModeTemplate = computed(() => {
  if (!isEditMode.value || !props.selectedCampaign) return null;

  const templateParams = props.selectedCampaign.template_params || {};
  const message = props.selectedCampaign.message || '';

  // Reconstruct a minimal template object that WhatsAppTemplateParser can use
  return {
    name: templateParams.name || 'Template',
    language: templateParams.language || 'en_US',
    category: templateParams.category || 'UTILITY',
    namespace: templateParams.namespace || '',
    components: [
      {
        type: 'BODY',
        text: message,
      },
    ],
  };
});

// The template to show in the parser
const templateForParser = computed(() => {
  return isEditMode.value ? editModeTemplate.value : selectedTemplate.value;
});

const dynamicVariables = CAMPAIGN_DYNAMIC_VARIABLES;

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return isEditMode.value || templateParserRef.value?.isFormInvalid === false;
});

const isFormInvalid = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const isInboxSelectionDisabled = computed(() => isEditMode.value);
const isTemplateSelectionDisabled = computed(() => isEditMode.value);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, { ...initialState });
  v$.value.$reset();
};

const prepareCampaignDetails = () => {
  const parserData = templateParserRef.value;

  // In edit mode, preserve original template info
  if (isEditMode.value && props.selectedCampaign) {
    const originalParams = props.selectedCampaign.template_params || {};
    const templateParams = {
      ...originalParams,
      processed_params:
        parserData?.processedParams || originalParams.processed_params || {},
    };

    const details = {
      title: state.title,
      message: parserData?.renderedTemplate || props.selectedCampaign.message,
      template_params: templateParams,
      scheduled_at: formatToUTCString(state.scheduledAt),
      conversation_label_id: state.conversationLabelId,
    };

    if (!isCsvCampaign.value) {
      details.audience = state.selectedAudience?.map(id => ({
        id,
        type: 'Label',
      }));
    }

    return details;
  }

  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    conversation_label_id: state.conversationLabelId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const validate = async () => {
  const fieldsValid = await v$.value.$validate();
  return fieldsValid && hasRequiredTemplateParams.value;
};

const copyToClipboard = async variableKey => {
  try {
    await navigator.clipboard.writeText(variableKey);
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.DYNAMIC_VARIABLES.COPIED'));
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.DYNAMIC_VARIABLES.COPY_FAILED'));
  }
};

// Emit update when form state changes
watch(
  () => [
    state.title,
    state.inboxId,
    state.templateId,
    state.scheduledAt,
    state.selectedAudience,
    state.conversationLabelId,
  ],
  () => {
    emit('update', prepareCampaignDetails());
  },
  { deep: true }
);

// Reset template selection when inbox changes (only in create mode)
watch(
  () => state.inboxId,
  (newValue, oldValue) => {
    if (!isEditMode.value && oldValue !== null) {
      state.templateId = null;
    }
  }
);

// Populate form when selectedCampaign changes (edit mode)
const updateStateFromCampaign = campaign => {
  if (!campaign) return;

  const audienceIds = (campaign.audience || [])
    .filter(item => item.type === 'Label')
    .map(item => item.id);

  // Convert timestamp to local datetime string (same pattern as currentDateTime)
  let scheduledAtLocal = null;
  if (campaign.scheduled_at) {
    const date = new Date(campaign.scheduled_at * 1000);
    scheduledAtLocal = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);
  }

  Object.assign(state, {
    title: campaign.title,
    inboxId: campaign.inbox?.id,
    templateId: null, // Template not editable, keep null
    scheduledAt: scheduledAtLocal,
    selectedAudience: audienceIds,
    conversationLabelId: campaign.conversation_label_id ?? null,
  });

  // Populate template variables
  nextTick(() => {
    if (templateParserRef.value && campaign.template_params?.processed_params) {
      const params = campaign.template_params.processed_params;
      if (params.body) {
        Object.assign(
          templateParserRef.value.processedParams.body || {},
          params.body
        );
      }
      if (params.header) {
        templateParserRef.value.processedParams.header = params.header;
      }
      if (params.buttons) {
        templateParserRef.value.processedParams.buttons = params.buttons;
      }
    }
  });
};

watch(
  () => props.selectedCampaign,
  newCampaign => {
    if (isEditMode.value && newCampaign) {
      updateStateFromCampaign(newCampaign);
    }
  },
  { immediate: true }
);

const resetToOriginal = () => {
  if (props.selectedCampaign) {
    updateStateFromCampaign(props.selectedCampaign);
  }
  v$.value.$reset();
};

defineExpose({
  validate,
  reset: resetState,
  resetToOriginal,
  isFormInvalid,
  prepareCampaignDetails,
});
</script>

<template>
  <form class="flex flex-col gap-4">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        :disabled="isInboxSelectionDisabled"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div v-if="!isEditMode" class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        :disabled="isTemplateSelectionDisabled"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser (only in create mode) -->
    <WhatsAppTemplateParser
      v-if="templateForParser && !isEditMode"
      ref="templateParserRef"
      allow-liquid
      :template="templateForParser"
    />

    <!-- Message Preview (only in edit mode) -->
    <div
      v-if="isEditMode && selectedCampaign?.message"
      class="flex flex-col gap-2"
    >
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.DETAILS.MESSAGE') }}
      </label>
      <div
        class="p-4 text-sm whitespace-pre-wrap rounded-lg bg-n-alpha-black2 text-n-slate-12 border border-n-weak"
      >
        {{ selectedCampaign.message }}
      </div>
    </div>

    <!-- Dynamic Variables Hint (only in create mode) -->
    <div
      v-if="templateForParser && !isEditMode"
      class="p-3 text-xs rounded-lg bg-n-alpha-2 border border-n-weak"
    >
      <p class="font-medium text-n-slate-12 mb-1.5">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DYNAMIC_VARIABLES.TITLE') }}
      </p>
      <p class="text-n-slate-11 mb-2">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DYNAMIC_VARIABLES.DESCRIPTION') }}
      </p>
      <div class="flex flex-wrap gap-1.5">
        <code
          v-for="variable in dynamicVariables"
          :key="variable.key"
          class="px-1.5 py-0.5 rounded font-mono text-[11px] cursor-pointer hover:brightness-110 transition-all"
          :class="
            variable.highlighted
              ? VARIABLE_CHIP_HIGHLIGHTED_CLASS
              : VARIABLE_CHIP_CLASS
          "
          :title="variable.description"
          @click="copyToClipboard(variable.key)"
        >
          {{ variable.key }}
        </code>
      </div>
    </div>

    <!-- CSV campaign indicator -->
    <div
      v-if="isCsvCampaign"
      class="flex flex-col gap-1 px-3 py-2 rounded-lg bg-n-blue-2 text-n-blue-11 text-xs"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-file-spreadsheet size-4 shrink-0" />
        <span class="font-medium">
          {{ t('CAMPAIGN.WHATSAPP.EDIT.CSV_CAMPAIGN_NOTICE') }}
        </span>
      </div>
      <span
        v-if="selectedCampaign?.campaign_import?.filename"
        class="pl-6 text-n-blue-10"
      >
        {{ selectedCampaign.campaign_import.filename }}
      </span>
    </div>

    <div v-if="!isCsvCampaign" class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-1.5">
        <label
          for="conversation-label"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.LABEL') }}
        </label>
        <button
          v-tooltip.top="{
            content: t(
              'CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.TOOLTIP'
            ),
            container: false,
            popperClass: 'max-w-xs',
          }"
          type="button"
          :aria-label="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.TOOLTIP_LABEL')
          "
          class="inline-flex items-center justify-center !p-0 rounded size-5 shrink-0 cursor-help text-n-slate-11 hover:text-n-slate-12 focus-visible:outline-n-brand"
        >
          <span class="i-lucide-info size-4" aria-hidden="true" />
        </button>
      </div>
      <ComboBox
        id="conversation-label"
        :model-value="state.conversationLabelId"
        :options="audienceList"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.PLACEHOLDER')
        "
        @update:model-value="state.conversationLabelId = $event || null"
      />
      <p class="mt-1 mb-0 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.HELP') }}
      </p>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />
  </form>
</template>
